import {
  CORRELATION_ID_HEADER,
  CSRF_COOKIE,
  CSRF_HEADER,
  isApiErrorBody,
  type ApiErrorCode,
  type ApiErrorDetail,
} from '@inventohub/shared';

/**
 * The one way the client talks to the API (AC 10).
 * Components never call `fetch` themselves, so error handling and CSRF stay in one place.
 */

export class ApiClientError extends Error {
  readonly code: ApiErrorCode;
  readonly httpStatus: number;
  readonly correlationId: string | null;
  readonly details: ApiErrorDetail[];

  constructor(
    code: ApiErrorCode,
    httpStatus: number,
    message: string,
    correlationId: string | null,
    details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.correlationId = correlationId;
    this.details = details;
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export function readCookie(cookieHeader: string, name: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    return decodeURIComponent(part.slice(separator + 1).trim());
  }
  return null;
}

function buildHeaders(method: string, hasBody: boolean): Headers {
  const headers = new Headers({ accept: 'application/json' });
  if (hasBody) headers.set('content-type', 'application/json');
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(document.cookie, CSRF_COOKIE);
    if (token) headers.set(CSRF_HEADER, token);
  }
  return headers;
}

export interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const hasBody = options.body !== undefined;
  // Built before the try so a programming error here is never mistaken for a network failure.
  const headers = buildHeaders(method, hasBody);

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      headers,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    // The request never reached the server. Nothing was saved, and we say so.
    throw new ApiClientError(
      'NETWORK_ERROR',
      0,
      'Network unavailable. Nothing was saved — reconnect and try again.',
      null,
    );
  }

  const correlationId = response.headers.get(CORRELATION_ID_HEADER);
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiClientError(
        payload.error.code,
        response.status,
        payload.error.message,
        payload.correlationId,
        payload.error.details ?? [],
      );
    }
    throw new ApiClientError(
      'INTERNAL_ERROR',
      response.status,
      'The server returned an unreadable error.',
      correlationId,
    );
  }

  return payload as T;
}
